/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState, useMemo } from "react";
import * as echarts from "echarts";
import { CallbackDataParams } from "echarts/types/dist/shared";
import { type SectorColorStyle } from "src/helpers/api/sharedFunction";

type SectorDataItem = {
  rsi_weekly: number | string;
  week: string;
};

type FormattedData = Record<string, SectorDataItem[]>;

interface ChartProps {
  selectedSector: string | null;
  highlightedSectors?: string[];
  highlightWeek?: string | null;
  sectorColors: {
    [key: string]: SectorColorStyle;
  };
  sectors: string[];
  rankingData: FormattedData;
  rankedData: Record<string, number[]>;
  darkMode: boolean | undefined;
  showLegend?: boolean;
  legendSectors?: string[];
  compact?: boolean;
}

function hexToRgb(hex: string) {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r},${g},${b}`;
}

function extractHex(color: string) {
  if (color.startsWith("#")) return color;
  const match = color.match(/#([0-9a-fA-F]{6})/);
  return match ? `#${match[1]}` : "#999999";
}

function getSectorLineGradient(
  sectorDef: ChartProps["sectorColors"][string] | undefined,
) {
  const topHex = extractHex(sectorDef?.bg || "#999999");
  const midHex = extractHex(sectorDef?.bgSolid || topHex);
  const bottomHex = extractHex(sectorDef?.bgEnd || topHex);

  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: topHex },
    { offset: 0.5, color: midHex },
    { offset: 1, color: bottomHex },
  ]);
}

function getRibbonShadowStyle(
  sectorDef: ChartProps["sectorColors"][string] | undefined,
  darkMode: boolean | undefined,
) {
  return {
    shadowColor:
      sectorDef?.ribbonDropShadow ??
      (darkMode ? "rgba(255, 255, 255, 0.25)" : "rgba(0, 0, 0, 0.35)"),
    shadowBlur: 14,
    shadowOffsetX: 0,
    shadowOffsetY: 4,
  };
}

function easeInOutSine(t: number): number {
  return -(Math.cos(Math.PI * t) - 1) / 2;
}

const WEEK_HIGHLIGHT_THEME = {
  dark: {
    fill: "45, 212, 191",
    glow: "0, 168, 95",
    label: "#5eead4",
  },
  light: {
    fill: "13, 148, 136",
    glow: "0, 168, 95",
    label: "#0f766e",
  },
} as const;

/** Week label blink cycle in ms — lower = faster (e.g. 800 ≈ 0.8s per pulse). */
const WEEK_LABEL_BLINK_MS = 2400;

function buildWeekMarkArea(week: string, darkMode: boolean | undefined) {
  const theme = darkMode
    ? WEEK_HIGHLIGHT_THEME.dark
    : WEEK_HIGHLIGHT_THEME.light;

  return {
    silent: true,
    animation: false,
    itemStyle: {
      color: `rgba(${theme.fill}, ${darkMode ? 0.08 : 0.06})`,
      borderWidth: 0,
    },
    data: [[{ xAxis: week }, { xAxis: week }]],
  };
}

function buildHighlightLabelRich(darkMode: boolean | undefined, pulse: number) {
  const theme = darkMode
    ? WEEK_HIGHLIGHT_THEME.dark
    : WEEK_HIGHLIGHT_THEME.light;
  const intensity = Math.min(1, Math.max(0, pulse));

  return {
    color: theme.label,
    fontWeight: "bold" as const,
    opacity: 0.35 + intensity * 0.65,
    textShadowColor: `rgba(${theme.glow}, ${0.15 + intensity * 0.85})`,
    textShadowBlur: 4 + intensity * 16,
  };
}

const RibbonChart: React.FC<ChartProps> = ({
  selectedSector,
  highlightedSectors = [],
  highlightWeek = null,
  sectorColors,
  sectors,
  rankingData,
  rankedData,
  darkMode,
  showLegend = true,
  legendSectors,
  compact = false,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);
  const [activeSector, setActiveSector] = useState<string | null>(
    selectedSector,
  );

  const legendData =
    legendSectors && legendSectors.length > 0 ? legendSectors : sectors;
  const hasFilteredLegend = Boolean(legendSectors && legendSectors.length > 0);
  const weekLabels = sectors.length
    ? rankingData[sectors[0]].map((d) => d.week)
    : [];
  const resolvedHighlightWeek =
    highlightWeek === undefined
      ? weekLabels.length > 0
        ? weekLabels[weekLabels.length - 1]
        : null
      : highlightWeek !== null && weekLabels.includes(highlightWeek)
        ? highlightWeek
        : null;

  const weekMarkArea = resolvedHighlightWeek
    ? buildWeekMarkArea(resolvedHighlightWeek, darkMode)
    : undefined;

  useEffect(() => {
    setActiveSector(selectedSector);
  }, [selectedSector]);

  const makeSeries: any = (sel: string | null) => {
    const effectiveHighlighted = sel ? [sel] : highlightedSectors;

    return sectors.map((sector, sectorIndex) => {
      const isSelected = effectiveHighlighted.includes(sector);
      const isFiltered = effectiveHighlighted.length > 0;
      const isEmbossed = compact && isFiltered && isSelected;

      const sectorDef = sectorColors[sector];
      const bgHex = extractHex(sectorDef?.bg || "#999999");
      const lineGradient = getSectorLineGradient(sectorDef);
      const ribbonShadow = getRibbonShadowStyle(sectorDef, darkMode);
      const textColor = "#000000";

      return {
        name: sector,
        type: "line",
        smooth: true,
        z: isSelected ? 3 : 1,
        markArea: sectorIndex === 0 ? weekMarkArea : undefined,
        lineStyle: {
          width: isEmbossed ? 10 : compact ? 8 : 18,
          opacity: isFiltered ? (isSelected ? 1 : 0.08) : 0.85,
          cap: "round",
          color: lineGradient,
          ...ribbonShadow,
          shadowBlur: isEmbossed ? 20 : ribbonShadow.shadowBlur,
          shadowOffsetY: isEmbossed ? 6 : ribbonShadow.shadowOffsetY,
        },
        itemStyle: {
          color: lineGradient,
          borderColor: sectorDef?.ribbonHighlightShadow ?? bgHex,
          borderWidth: isEmbossed ? 2 : 1,
          shadowColor: ribbonShadow.shadowColor,
          shadowBlur: isEmbossed ? 16 : 10,
          shadowOffsetX: 0,
          shadowOffsetY: isEmbossed ? 5 : 3,
        },
        symbol: "rect",
        symbolSize: isFiltered
          ? isSelected
            ? compact
              ? [22, 11]
              : [36, 18]
            : 0
          : compact
            ? [20, 10]
            : [36, 18],
        symbolOffset: [0, 0],
        silent: isFiltered ? !isSelected : false,
        emphasis: { disabled: isFiltered ? !isSelected : false },

        label: {
          show: true,
          position: "inside",
          fontSize: compact ? 10 : 12,
          color: isFiltered ? (isSelected ? textColor : "none") : textColor,
          backgroundColor: `rgba(${hexToRgb(bgHex)}, ${1})`,
          opacity: isFiltered ? (isSelected ? 1 : 0.4) : 0.85,
          borderRadius: 4,
          padding: [2, 6],
          formatter: (params: { dataIndex: number }) =>
            rankingData[sector][params.dataIndex].rsi_weekly,
        },
        data: rankedData[sector],
        color: isFiltered ? (isSelected ? lineGradient : "none") : lineGradient,
      };
    });
  };

  const baseOption: echarts.EChartsOption = {
    tooltip: {
      trigger: "item",
      confine: true,
      axisPointer: { type: "line" },
      formatter: (
        params: CallbackDataParams | CallbackDataParams[],
      ): string => {
        const paramArray = Array.isArray(params) ? params : [params];

        return paramArray
          .map((point) => {
            const sector = point.seriesName as string;
            const currIndex = point.dataIndex!;
            const currData = rankingData[sector][currIndex];
            const prevData = rankingData[sector][currIndex - 1] ?? currData;

            const currWeek = currData.week;
            const prevWeek = prevData.week;

            // const currValue = currData.rsi_weekly;
            // const prevValue = prevData.rsi_weekly;

            const currRank = rankedData[sector][currIndex];
            const prevRank = rankedData[sector][currIndex - 1] ?? currRank;

            // const valueChange = currValue - prevValue;
            // const valueChangePct =
            //   prevValue !== 0
            //     ? ((valueChange / prevValue) * 100).toFixed(2)
            //     : "0";

            const rankChange = currRank - prevRank;

            const arrow =
              rankChange === 0 ? "➡️" : prevRank > currRank ? "⬆️" : "⬇️";

            return `
        ${currWeek}<br/>
        Sector Name: ${sector}<br/>
     
        ${prevWeek} ${sector} Rank: ${prevRank} <br/>
        ${currWeek} ${sector} Rank: ${currRank}<br/>
        Rank Change: ${arrow} ${Math.abs(rankChange)}
      `;
          })
          .join("<hr/>");
      },
    },
    legend: {
      show: showLegend && legendData.length > 0,
      data: legendData,
      type: hasFilteredLegend ? "plain" : "scroll",
      top: 0,
      left: "center",
      textStyle: {
        fontSize: compact ? 10 : 12,
        color: darkMode ? "white" : "black",
      },
    },
    grid: {
      left: 0,
      right: 50,
      bottom: 10,
      top: hasFilteredLegend && showLegend ? 32 : 16,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: sectors.length ? rankingData[sectors[0]].map((d) => d.week) : [],
      axisLabel: {
        fontSize: 12,
        formatter: (value: string) => {
          const [year, week] = value.split(" ");
          if (value === resolvedHighlightWeek) {
            return `{hl|${year}\n${week}}`;
          }
          return `${year}\n${week}`;
        },
        rich: {
          hl: buildHighlightLabelRich(darkMode, 0.5),
        },
        color: darkMode ? "white" : "black",
      },
      splitLine: { show: false },
    },

    yAxis: {
      type: "value",
      inverse: true,
      interval: 1,
      min: 1,
      show: false,
      max: sectors.length + 1,
      axisLabel: {
        margin: 30,
        fontSize: 12,
        formatter: (v: number) => `#${v}`,
      },
    },
    labelLayout: { hideOverlap: true },
    series: makeSeries(activeSector),
  };

  useEffect(() => {
    if (chartRef.current && !chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
      chartInstance.current.setOption(baseOption);

      chartInstance.current.on("legendselectchanged", (params: any) => {
        const clickedName = params.name;
        if (!params.selected[clickedName]) {
          chartInstance.current?.dispatchAction({
            type: "legendSelect",
            name: clickedName,
          });
        }
        setActiveSector((prev) => (prev === clickedName ? "" : clickedName));
      });
    }
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [darkMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.setOption(baseOption, true);
    }
  }, [rankedData, activeSector, highlightedSectors, highlightWeek, darkMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!chartInstance.current || !resolvedHighlightWeek) {
      return;
    }

    const chart = chartInstance.current;
    let frameId = 0;
    let cancelled = false;
    const blinkDuration = WEEK_LABEL_BLINK_MS;

    // Throttle to ~15 FPS to prevent severe main-thread blocking and MarketTicker stutter
    const THROTTLE_MS = 66;
    let lastUpdateTime = 0;

    const tick = (now: number) => {
      if (cancelled) return;

      if (now - lastUpdateTime > THROTTLE_MS) {
        lastUpdateTime = now;

        const phase = (now % blinkDuration) / blinkDuration;
        const triangle = phase < 0.5 ? phase * 2 : 2 - phase * 2;
        const pulse = easeInOutSine(triangle);

        chart.setOption(
          {
            xAxis: {
              axisLabel: {
                rich: {
                  hl: buildHighlightLabelRich(darkMode, pulse),
                },
              },
            },
          },
          false,
        );
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [resolvedHighlightWeek, darkMode]);

  useEffect(() => {
    if (!chartInstance.current) return;

    const chart = chartInstance.current;

    const newSeries = makeSeries(activeSector);
    const selected = newSeries.find((s: any) => s.name === activeSector);

    if (!selected) return;

    const realData = selected.data;
    const fakeData = realData.map(() => 0);

    chart.setOption({
      series: [
        {
          name: activeSector,
          data: fakeData,
          animation: true,
          animationDuration: 300,
        },
      ],
    });

    setTimeout(() => {
      chart.setOption({
        series: [
          {
            name: activeSector,
            data: realData,
            animationDuration: 1200,
            animationEasing: "cubicOut",
          },
        ],
      });
    }, 50);
  }, [activeSector]); // eslint-disable-line react-hooks/exhaustive-deps

  const dynamicHeight = compact
    ? Math.max(280, sectors.length * 28)
    : Math.max(300, sectors.length * 30);

  useEffect(() => {
    if (chartInstance.current) {
      // Timeout gives the DOM a millisecond to apply the new style height
      setTimeout(() => {
        chartInstance.current?.resize();
      }, 50);
    }
  }, [sectors.length]); // Triggers resize whenever sector count changes

  const isDataMissing = useMemo(() => {
    return Object.values(rankingData).some((sectorData) =>
      sectorData.some(
        (d) =>
          d.rsi_weekly === null ||
          d.rsi_weekly === undefined ||
          Number(d.rsi_weekly) === 0,
      ),
    );
  }, [rankingData]);

  return (
    <div className="relative w-full" style={{ height: `${dynamicHeight}px` }}>
      {isDataMissing && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/50 dark:bg-[#121419]/80 backdrop-blur-[2px]">
          <p className="text-base font-semibold text-gray-700 dark:text-gray-200">
            The dataset is being updated. Please check back shortly
          </p>
        </div>
      )}
      <div ref={chartRef} className="w-full h-full" />
    </div>
  );
};

export default React.memo(RibbonChart);
